import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Moon, Mail, ShieldCheck, ChevronDown } from 'lucide-react';
type LegalKey = 'terminos' | 'privacidad' | 'reembolso' | null;
const legalContent: Record<Exclude<LegalKey, null>, {
  title: string;
  body: string[];
}> = {
  terminos: {
    title: 'Términos y Condiciones',
    body: ['Al realizar una compra en GuiaDownload.com aceptas los presentes términos y condiciones. El producto "Tu Bebé Sí Puede Dormir" es una guía digital en formato PDF de uso personal e intransferible.', 'GuiaDownload.com ofrece este material con fines informativos y educativos. La guía no reemplaza la asesoría de un pediatra o profesional de la salud. Cada bebé es único y los resultados pueden variar según la implementación de las rutinas.', 'Está prohibida la redistribución, reventa o reproducción total o parcial del contenido sin autorización escrita de GuiaDownload.com. Todos los derechos de propiedad intelectual pertenecen a GuiaDownload.com.', 'Al completar la compra recibirás acceso digital inmediato al PDF a través del correo electrónico proporcionado en el checkout de Shopify.']
  },
  privacidad: {
    title: 'Política de Privacidad',
    body: ['GuiaDownload.com respeta tu privacidad. Solo recopilamos la información estrictamente necesaria para procesar tu compra: nombre, correo electrónico y datos de pago (gestionados de forma segura por Shopify).', 'No vendemos, alquilamos ni compartimos tu información personal con terceros con fines comerciales. Tus datos se utilizan únicamente para entregar el producto, brindar soporte y, si lo aceptas, enviarte contenido relacionado con el cuidado del sueño infantil.', 'Puedes solicitar la eliminación de tus datos en cualquier momento escribiendo a guiadownloadnow@gmail.com.']
  },
  reembolso: {
    title: 'Política de Reembolso',
    body: ['Debido a la naturaleza digital del producto, una vez entregado el acceso al PDF las ventas son finales. Sin embargo, en GuiaDownload.com queremos que estés conforme con tu compra.', 'Si tienes algún problema con la descarga o acceso al material, contáctanos dentro de los 7 días posteriores a la compra y resolveremos cualquier inconveniente técnico de forma prioritaria.', 'Para cualquier solicitud relacionada con tu pedido, escríbenos a guiadownloadnow@gmail.com con el correo y número de orden de Shopify.']
  }
};
export function Footer() {
  const [openSection, setOpenSection] = useState<LegalKey>(null);
  const toggle = (key: Exclude<LegalKey, null>) => {
    setOpenSection(prev => prev === key ? null : key);
  };
  return <footer className="bg-white border-t border-surface" data-id="element-30">
      <div className="max-w-4xl mx-auto px-5 py-12 md:py-16" data-id="element-31">
        {/* Brand */}
        <div className="text-center mb-10" data-id="element-32">
          <div className="inline-flex items-center justify-center w-12 h-12 bg-surface rounded-2xl mb-4" data-id="element-33">
            <Moon className="w-6 h-6 text-terracotta" fill="currentColor" data-id="element-34" />
          </div>
          <h3 className="font-serif text-2xl font-semibold text-heading mb-1" data-id="element-35">
            GuiaDownload<span className="text-terracotta" data-id="element-36">.com</span>
          </h3>
          <p className="text-sm text-body max-w-sm mx-auto" data-id="element-37">
            Guías digitales prácticas para mamás reales. Hechas con cariño,
            pensadas para tu día a día.
          </p>
        </div>

        {/* Trust badges */}
        <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-3 text-xs text-body font-medium mb-10 pb-10 border-b border-surface" data-id="element-38">
          <span className="flex items-center" data-id="element-39">
            <ShieldCheck className="w-4 h-4 mr-1.5 text-terracotta" data-id="element-40" />
            Pago seguro con Shopify
          </span>
          <span className="flex items-center" data-id="element-41">
            <Mail className="w-4 h-4 mr-1.5 text-terracotta" data-id="element-42" />
            guiadownloadnow@gmail.com
          </span>
        </div>

        {/* Legal accordion */}
        <div className="space-y-2 mb-10" data-id="element-43">
          {(Object.keys(legalContent) as Array<Exclude<LegalKey, null>>).map(key => {
          const {
            title,
            body
          } = legalContent[key];
          const isOpen = openSection === key;
          return <div key={key} className="border border-surface rounded-2xl overflow-hidden bg-cream/50" data-id="element-44">
                  <button type="button" onClick={() => toggle(key)} aria-expanded={isOpen} className="w-full flex items-center justify-between px-5 py-4 text-left" data-id="element-45">
                    <span className="font-serif text-base font-semibold text-heading" data-id="element-46">
                      {title}
                    </span>
                    <ChevronDown className={`w-4 h-4 text-body transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`} data-id="element-47" />
                  </button>

                  <AnimatePresence initial={false} data-id="element-48">
                    {isOpen && <motion.div key="content" initial={{
                height: 0,
                opacity: 0
              }} animate={{
                height: 'auto',
                opacity: 1
              }} exit={{
                height: 0,
                opacity: 0
              }} transition={{
                duration: 0.3,
                ease: 'easeInOut'
              }} className="overflow-hidden" data-id="element-49">
                        <div className="px-5 pb-5 pt-0 space-y-3 text-sm text-body leading-relaxed" data-id="element-50">
                          {body.map((paragraph, i) => <p key={i} data-id="element-51">{paragraph}</p>)}
                        </div>
                      </motion.div>}
                  </AnimatePresence>
                </div>;
        })}
        </div>

        {/* Disclaimer */}
        <p className="text-xs text-body/70 text-center max-w-2xl mx-auto mb-8 leading-relaxed" data-id="element-52">
          Este producto digital es ofrecido por{' '}
          <span className="font-semibold text-heading" data-id="element-53">GuiaDownload.com</span> y
          tiene fines informativos. No reemplaza la asesoría de un pediatra o
          profesional de la salud.
        </p>

        {/* Copyright */}
        <div className="flex flex-col md:flex-row items-center justify-between gap-3 pt-6 border-t border-surface text-xs text-body/70" data-id="element-54">
          <p data-id="element-55">
            © {new Date().getFullYear()}{' '}
            <span className="font-semibold text-heading" data-id="element-56">GuiaDownload.com</span>{' '}
            · Todos los derechos reservados.
          </p>
          <p data-id="element-57">Hecho con cariño para mamás latinas 💛</p>
        </div>
      </div>
    </footer>;
}